import {
  createUserWithEmailAndPassword,
  updateProfile,
  signInWithEmailAndPassword,
} from "firebase/auth";
import {
  Timestamp,
  collection,
  doc,
  arrayUnion,
  runTransaction,
  query,
  where,
  limit,
  getDoc,
  updateDoc,
  setDoc,
  getDocs,
} from "firebase/firestore";

import { auth, db } from "../../config/firebase";
import { verifyInvitation } from "../invitationService";
import { Profile, createProfile } from "../profileService";
import { createWallet } from "../walletService";
import { mapSingleRole } from "../../utils/common";

/**
 * Completes user registration from an invitation or for an organization owner
 */

export async function completeRegistration(params: {
  invitationId?: string;
  token?: string;
  subscriptionId?: string;
  firstName: string;
  email?: string;
  password: string;
  company?: string;
  specialities?: string[];
  otherSpecify?: string;
  ratePerMinute?: string;
  bio?: string;
}) {
  const {
    invitationId,
    token,
    subscriptionId,
    firstName,
    email,
    password,
    company,
    specialities,
    otherSpecify,
    ratePerMinute,
    bio,
  } = params;

  // Determine registration type: invitation-based or subscription-based (owner)
  if (invitationId && token) {
    return handleInvitationRegistration({
      invitationId,
      token,
      firstName,
      password,
      specialities,
      otherSpecify,
      ratePerMinute,
      bio,
    });
  } else if (subscriptionId) {
    return handleOwnerRegistration({
      subscriptionId,
      firstName,
      email,
      password,
      company,
    });
  } else {
    throw new Error(
      "Invalid registration parameters. Either invitation details or subscription ID is required."
    );
  }
}

/**
 * Handles invitation-based registration
 */
async function handleInvitationRegistration(params: {
  invitationId: string;
  token: string;
  firstName: string;
  password: string;
  specialities?: string[];
  otherSpecify?: string;
  ratePerMinute?: string;
  bio?: string;
}) {
  const {
    invitationId,
    token,
    firstName,
    password,
    specialities,
    otherSpecify,
    ratePerMinute,
    bio,
  } = params;

  try {
    // 1. Verify the invitation is still valid
    const verificationResult = await verifyInvitation(invitationId, token);
    if (!verificationResult.valid) {
      throw new Error(verificationResult.error);
    }

    const invitation = verificationResult.invitation;

    // 2. Create the user account in Firebase Auth
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      invitation?.email,
      password
    );

    const uid = userCredential.user.uid;

    // Update display name
    await updateProfile(userCredential.user, {
      displayName: `${firstName} `,
    });

    const now = Timestamp.now();

    // 3. Create user document in Firestore
    const usersCollection = collection(db, "users");
    const userRef = doc(usersCollection, uid);

    // Base user data
    const userData: any = {
      uid,
      email: invitation?.email,
      name: firstName,
      organizations: [
        {
          orgId: invitation?.orgId,
          role: invitation?.role,
          joinedAt: now,
        },
      ],
      orgId: invitation?.orgId,
      uiRole: invitation?.teamRole,
      teamRole: invitation?.teamRole,
      teamId: invitation?.teamId || teamId, // Use provided teamId if available
      role: invitation?.role,

      createdAt: now,
      updatedAt: now,
    };

    // Add expert-specific fields if this is an expert registration
    if (invitation?.role === "Expert") {
      userData.name = firstName;
      userData.specialities = specialities || [];
      userData.otherSpecify = otherSpecify || "";
      userData.ratePerMinute = ratePerMinute || "";
      userData.bio = bio || "";
    }

    await setDoc(userRef, userData);

    // Create profile data based on Profile type
    const profileData: Profile = {
      type: invitation?.role,
      name: firstName,
      lastName: "",
      email: invitation?.email,
      organizationName: "", // Will be updated later
      teamName: "",
      teamRole: invitation?.teamRole || "",
      rating: 0,
      reviewCount: 0,
      createdAt: now,
      updatedAt: now,
    };

    // Add expert-specific fields if applicable
    if (invitation?.role === "Expert") {
      profileData.name = firstName;
      profileData.specialities = specialities || [];
      profileData.otherSpecify = otherSpecify || "";
      profileData.ratePerMinute = ratePerMinute ? parseFloat(ratePerMinute) : 0;
      profileData.bio = bio || "";
      profileData.availability = true; // Default availability
    }
    // Add userId for query support
    profileData.userId = uid;
    // Save profile in 'profiles' collection with userId as document ID
    const profilesCollection = collection(db, "profiles");
    const profileRef = doc(profilesCollection, uid);
    await setDoc(profileRef, profileData);

    // Query the profile by userId (example usage)
    const profileQuery = query(profilesCollection, where("userId", "==", uid));
    const profileSnapshot = await getDocs(profileQuery);
    // profileSnapshot.docs[0]?.data() contains the profile data if found
    console.log(
      "Profile data after registration:",
      profileSnapshot.docs[0]?.data()
    );

    const walletRole = mapSingleRole(invitation?.teamRole);

    // Create wallet - handle "unknown" role by defaulting to "user"
    const finalWalletRole = walletRole === "unknown" ? "user" : walletRole;
    await createWallet(finalWalletRole, uid);

    // 4. Add user to the team (create team if it doesn't exist)
    const orgsCollection = collection(db, "organizations");
    const orgDoc = doc(orgsCollection, invitation?.orgId);
    const teamsCollection = collection(orgDoc, "teams");
    const finalTeamId = invitation?.teamId;
    const teamRef = doc(teamsCollection, finalTeamId);

    // Get current team data
    const teamDoc = await getDoc(teamRef);

    if (!teamDoc.exists()) {
      // Team does not exist, no action taken here
    } else {
      // Add user to existing team
      const teamData = teamDoc.data();
      const updatedUsers = [
        ...teamData.users,
        {
          userId: uid,
          role: invitation?.teamRole,
          joinedAt: now,
        },
      ];

      await updateDoc(teamRef, {
        users: updatedUsers,
        updatedAt: now,
      });
    } // 5. Update the organization's users array and get planId
    const orgRef = doc(orgsCollection, invitation?.orgId);
    const orgDocSnapshot = await getDoc(orgRef);

    if (!orgDocSnapshot.exists()) {
      throw new Error("Organization not found");
    }

    const orgData = orgDocSnapshot.data();
    const subscriptionId = orgData?.planId;
    console.log(orgData, "subscriptionId");
    await updateDoc(orgRef, {
      users: arrayUnion({
        userId: uid,
        role: invitation?.role,
        joinedAt: now,
      }),
      updatedAt: now,
    });

    // 5.1. Call Firebase function to add subscription seat
    if (subscriptionId) {
      try {
        const response = await fetch(
          `https://us-central1-supporty-staging.cloudfunctions.net/addSubscriptionUser`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              addUsers: 1,
              subscriptionId: subscriptionId,
            }),
          }
        );

        if (!response.ok) {
          console.error(
            "Failed to add subscription seat:",
            response.statusText
          );
          // Don't throw error here to avoid breaking registration process
        } else {
          const result = await response.json();
          console.log("Successfully added subscription seat:", result);
        }
      } catch (error) {
        console.error("Error calling addSubscription function:", error);
        // Don't throw error here to avoid breaking registration process
      }
    } else {
      console.warn("No planId found in organization data");
    }

    // 6. Update invitation status to accepted
    const invitationsCollection = collection(db, "invitations");
    const invitationRef = doc(invitationsCollection, invitationId);

    await updateDoc(invitationRef, {
      status: "accepted",
      updatedAt: now,
      acceptedAt: now,
    });

    // 7. Return success response
    return {
      success: true,
      uid,
      email: invitation?.email,
      orgId: invitation?.orgId,
      planType: invitation?.planType,
      isNewUser: true,
    };
  } catch (error) {
    console.error("Registration error:", error);
    throw new Error("Failed to complete registration. " + error.message);
  }
}

/**
 * Handles owner registration from subscription ID
 */
async function handleOwnerRegistration(params: {
  subscriptionId: string;
  firstName: string;
  email: string;
  password: string;
  company?: string;
}) {
  const { subscriptionId, firstName, email, password, company } = params;

  try {
    // Get organization reference
    const orgRef = doc(db, "organizations", subscriptionId);
    const orgDoc = await getDoc(orgRef);

    if (!orgDoc.exists()) {
      throw new Error("Organization not found");
    }

    const organization = orgDoc.data();
    // 1. Create the user account in Firebase Auth
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );

    const uid = userCredential.user.uid;

    // Update display name
    await updateProfile(userCredential.user, {
      displayName: `${firstName}`,
    });

    const now = Timestamp.now();

    // 2. Create user document in Firestore
    const userRef = doc(db, "users", uid);
    const userData = {
      uid,
      email: email || "",
      name: firstName || "",
      organizations: [
        {
          orgId: subscriptionId,
          role:
            organization.planType === "expert"
              ? "expert_owner"
              : "customer_owner",
          joinedAt: now,
        },
      ],
      teamRole:
        organization.planType === "expert" ? "expert_owner" : "customer_owner",
      uiRole:
        organization.planType === "expert" ? "expert_owner" : "customer_owner",
      role: organization.planType === "expert" ? "Expert" : "Customer",
      createdAt: now,
      teamId: uid,
      updatedAt: now,
      orgId: subscriptionId,
    };
    await createWallet("organization", uid);
    // Remove undefined values to prevent Firestore errors
    Object.keys(userData).forEach((key) => {
      if (userData[key] === undefined) {
        delete userData[key];
      }
    });

    await setDoc(userRef, userData);

    // Create profile document with auto-generated ID

    // Create profile data based on Profile type
    const profileData: Profile = {
      type: organization.planType === "expert" ? "Expert" : "Customer",
      name: firstName,
      lastName: "",
      email: email,
      organizationName: company || "",
      teamName: "Default Team",
      teamRole:
        organization.planType === "expert" ? "expert_owner" : "customer_owner", // Owner is always team lead
      rating: 0,
      reviewCount: 0,
      createdAt: now,
      updatedAt: now,
    };

    // Add expert-specific fields if applicable
    if (organization.planType === "expert") {
      profileData.availability = true; // Default availability
      profileData.specialities = [];
      profileData.ratePerMinute = 0;
    }
    await createProfile(uid, profileData);

    // 3. Check for the default team in the organization
    const teamsCollection = collection(orgRef, "teams");
    const teamsQuerySnapshot = await getDocs(
      query(teamsCollection, where("default", "==", true), limit(1))
    );

    if (teamsQuerySnapshot.empty) {
      // Create default team if it doesn't exist
      // const defaultTeamRef = doc(teamsCollection);
      // await setDoc(defaultTeamRef, {
      //   teamName: "Default Team",
      //   default: true,
      //   users: [],
      //   createdAt: now,
      //   updatedAt: now,
      // });
    } else {
      // Add owner to the existing default team
      const teamDoc = teamsQuerySnapshot.docs[0];
      const teamData = teamDoc.data();
      const updatedUsers = [
        ...teamData.users,
        {
          userId: uid,
          role: "team_lead", // Owner is always team lead
          joinedAt: now,
        },
      ];
      await updateDoc(teamDoc.ref, {
        users: updatedUsers,
        updatedAt: now,
      });
    }

    // 4. Update the organization
    await updateDoc(orgRef, {
      users: arrayUnion({
        userId: uid,
        role:
          organization.planType === "expert"
            ? "expert_owner"
            : "customer_owner",
        joinedAt: now,
        company: company || null,
      }),
      "owner.accountCreated": true,
      "owner.owner": uid,
      name: company,
      updatedAt: now,
    });

    return {
      success: true,
      uid,
      email,
      orgId: subscriptionId,
      planType: organization.planType,
      isNewUser: true,
    };
  } catch (error) {
    console.error("Owner registration error:", error);
    throw new Error("Failed to complete owner registration. " + error.message);
  }
}

/**
 * Logs in a user after registration
 */
/**
 * Handles owner registration from subscription ID
 */

export async function loginAfterRegistration(email: string, password: string) {
  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    return {
      success: true,
      user: userCredential.user,
    };
  } catch (error) {
    console.error("Login error:", error);
    return {
      success: false,
      error: "Failed to log in after registration",
    };
  }
}

/**
 * Query a profile from the 'profiles' collection by userId
 */
export async function getProfileByUserId(userId: string) {
  const profilesRef = collection(db, "profiles");
  const profileQuery = query(profilesRef, where("userId", "==", userId));
  const querySnapshot = await getDocs(profileQuery);
  if (querySnapshot.empty) return null;
  // Return the first matching profile (or all if you want)
  return querySnapshot.docs[0].data();
}
